/**
 * CR226: Config Debug Bar (Enhanced)
 * 
 * Shows complete source-of-truth diagnostics:
 * - templateIdUsed: Which DB template is being used
 * - templateUpdatedAt: When it was last saved
 * - configHash: Proves draft binding is working
 * - sourceOfTruth: "db" | "draftOverlay" | "fallback"
 */

import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle2, 
  XCircle, 
  Hash, 
  Clock, 
  Database,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import type { CardVisibility } from "@/lib/deals/cardVisibility";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** CR226: Source of truth indicator */
export type SourceOfTruth = "db" | "draftOverlay" | "fallback";

interface ConfigDebugBarProps {
  dealType: string;
  configHash: string;
  lastSavedAt: string | null;
  templateId?: string | null;
  templateUpdatedAt?: string | null;
  sourceOfTruth: SourceOfTruth;
  visibility: Partial<CardVisibility>;
  hasUnsavedChanges: boolean;
  className?: string;
  /** CR229: Current section order for debug display */
  sectionOrder?: string[];
}

const VISIBILITY_KEYS: { key: keyof CardVisibility; label: string; short: string }[] = [
  { key: "showRibbon", label: "Ribbon", short: "R" },
  { key: "showStarburst", label: "Starburst", short: "S" },
  { key: "showMerchantName", label: "Name", short: "N" },
  { key: "showMerchantLogo", label: "Logo", short: "L" },
  { key: "showHeadline", label: "Headline", short: "H" },
  { key: "showDates", label: "Dates", short: "D" },
];

export function ConfigDebugBar({
  dealType,
  configHash,
  lastSavedAt,
  templateId,
  templateUpdatedAt,
  sourceOfTruth,
  visibility,
  hasUnsavedChanges,
  className,
  sectionOrder,
}: ConfigDebugBarProps) {
  const lastSavedText = lastSavedAt
    ? formatDistanceToNow(new Date(lastSavedAt), { addSuffix: true })
    : "Never";

  const templateUpdatedText = templateUpdatedAt
    ? formatDistanceToNow(new Date(templateUpdatedAt), { addSuffix: true })
    : null;
  
  // CR229: Generate short section order string for display
  const sectionOrderText = sectionOrder 
    ? sectionOrder.slice(0, 5).map(s => s.slice(0, 3)).join("→") + (sectionOrder.length > 5 ? "…" : "")
    : null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn(
        "flex flex-wrap items-center gap-2 text-[10px] bg-muted/50 border rounded px-2 py-1.5",
        className
      )}>
        {/* Deal Type */}
        <div className="flex items-center gap-1">
          <Database className="h-3 w-3 text-muted-foreground" />
          <code className="font-mono text-muted-foreground">{dealType}</code>
        </div>

        <span className="text-muted-foreground/50">|</span>

        {/* CR226: Source of Truth Badge */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={cn(
                "h-4 px-1.5 text-[9px] cursor-help",
                sourceOfTruth === "db" && "bg-success/20 text-success",
                sourceOfTruth === "draftOverlay" && "bg-primary/20 text-primary",
                sourceOfTruth === "fallback" && "bg-warning/20 text-warning"
              )}
            >
              {sourceOfTruth === "db" ? "DB" : sourceOfTruth === "draftOverlay" ? "Draft" : "Fallback"}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-semibold">Source: {sourceOfTruth}</p>
            <p className="text-xs text-muted-foreground">
              {sourceOfTruth === "db" && "Reading from saved DB template"}
              {sourceOfTruth === "draftOverlay" && "Draft changes over DB"}
              {sourceOfTruth === "fallback" && "⚠️ No DB template"}
            </p>
          </TooltipContent>
        </Tooltip>

        <span className="text-muted-foreground/50">|</span>

        {/* CR226: Template ID */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <FileText className="h-3 w-3 text-muted-foreground" />
              <code className="font-mono text-primary">
                {templateId ? templateId.slice(0, 8) : "—"}
              </code>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-semibold">Template ID</p>
            <p className="text-xs text-muted-foreground break-all max-w-[200px]">
              {templateId || "No DB template"}
            </p>
            {templateUpdatedText && (
              <p className="text-xs text-muted-foreground mt-1">Saved: {templateUpdatedText}</p>
            )}
          </TooltipContent>
        </Tooltip>

        <span className="text-muted-foreground/50">|</span>

        {/* Config Hash */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <Hash className="h-3 w-3 text-muted-foreground" />
              <code className="font-mono text-primary">{configHash}</code>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Config hash - changes when settings change</p>
          </TooltipContent>
        </Tooltip>

        <span className="text-muted-foreground/50">|</span>

        {/* Last Saved */}
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">{lastSavedText}</span>
        </div>

        {hasUnsavedChanges && (
          <Badge variant="destructive" className="h-4 px-1 text-[9px]">Unsaved</Badge>
        )}

        {sourceOfTruth === "fallback" && (
          <AlertTriangle className="h-3.5 w-3.5 text-warning" />
        )}

        <span className="text-muted-foreground/50">|</span>

        {/* Quick Visibility */}
        <div className="flex items-center gap-0.5">
          <span className="text-muted-foreground mr-1">Vis:</span>
          {VISIBILITY_KEYS.map((v) => {
            const isOn = visibility[v.key] ?? true;
            return (
              <Tooltip key={v.key}>
                <TooltipTrigger asChild>
                  <span className={cn("font-bold cursor-help px-0.5", isOn ? "text-success" : "text-muted-foreground/30")}>
                    {v.short}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <div className="flex items-center gap-1 text-xs">
                    {isOn ? <CheckCircle2 className="h-3 w-3 text-success" /> : <XCircle className="h-3 w-3 text-muted-foreground" />}
                    {v.label}: {isOn ? "ON" : "OFF"}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* CR229: Section Order Debug */}
        {sectionOrder && sectionOrder.length > 0 && (
          <>
            <span className="text-muted-foreground/50">|</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-help">
                  <span className="text-muted-foreground">Order:</span>
                  <code className="font-mono text-primary text-[9px]">{sectionOrderText}</code>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[300px]">
                <p className="text-xs font-semibold mb-1">Section Render Order:</p>
                <ol className="text-xs text-muted-foreground space-y-0.5">
                  {sectionOrder.map((section, idx) => (
                    <li key={section} className="flex items-center gap-2">
                      <span className="text-primary font-mono w-4">{idx + 1}.</span>
                      <span>{section}</span>
                    </li>
                  ))}
                </ol>
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
